import { ShiftOptions } from 'models/ICattlesData'
import { IUserCompany } from 'models/IUserData'
import moment from 'moment'

export const MILKING_SHIFT_LABELS: Record<ShiftOptions, string> = {
	[ShiftOptions.AM]: 'Утренняя',
	[ShiftOptions.PM]: 'Дневная',
	[ShiftOptions.NIGHT]: 'Ночная',
}

type ShiftPeriod = {
	key: ShiftOptions
	start?: string
	end?: string
}

export type MilkingShiftCompanyPeriods = Pick<
	IUserCompany,
	| 'am_period_start'
	| 'am_period_end'
	| 'pm_period_start'
	| 'pm_period_end'
	| 'night_period_start'
	| 'night_period_end'
>

const FALLBACK_SHIFT_PERIODS: Required<ShiftPeriod>[] = [
	{ key: ShiftOptions.AM, start: '05:00:00', end: '13:00:00' },
	{ key: ShiftOptions.PM, start: '13:00:00', end: '21:00:00' },
	{ key: ShiftOptions.NIGHT, start: '21:00:00', end: '05:00:00' },
]

const timeToMinutes = (time: string): number | null => {
	const [hoursRaw, minutesRaw = '0', secondsRaw = '0'] = time.split(':')
	const hours = Number(hoursRaw)
	const minutes = Number(minutesRaw)
	const seconds = Number(secondsRaw)

	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		Number.isNaN(seconds) ||
		hours < 0 ||
		hours > 23
	) {
		return null
	}

	return hours * 60 + minutes + seconds / 60
}

const isMinutesInRange = (
	value: number,
	start: number,
	end: number,
): boolean => {
	if (start === end) return false
	if (start < end) return value >= start && value < end
	return value >= start || value < end
}

const getConfiguredPeriods = (
	company?: MilkingShiftCompanyPeriods | null,
): Required<ShiftPeriod>[] => {
	const periods: ShiftPeriod[] = [
		{
			key: ShiftOptions.AM,
			start: company?.am_period_start,
			end: company?.am_period_end,
		},
		{
			key: ShiftOptions.PM,
			start: company?.pm_period_start,
			end: company?.pm_period_end,
		},
		{
			key: ShiftOptions.NIGHT,
			start: company?.night_period_start,
			end: company?.night_period_end,
		},
	]

	return periods.filter(
		(period): period is Required<ShiftPeriod> =>
			Boolean(period.start?.trim()) && Boolean(period.end?.trim()),
	)
}

export const getMilkingShiftByTimestamp = (
	timestamp: number,
	company?: MilkingShiftCompanyPeriods | null,
): ShiftOptions | null => {
	if (!Number.isFinite(timestamp)) return null

	const milkingMoment = moment(timestamp)
	if (!milkingMoment.isValid()) return null

	const minutes =
		milkingMoment.hours() * 60 +
		milkingMoment.minutes() +
		milkingMoment.seconds() / 60

	const configuredPeriods = getConfiguredPeriods(company)
	const periodsToCheck =
		configuredPeriods.length > 0 ? configuredPeriods : FALLBACK_SHIFT_PERIODS

	for (const period of periodsToCheck) {
		const start = timeToMinutes(period.start)
		const end = timeToMinutes(period.end)
		if (start == null || end == null) continue
		if (isMinutesInRange(minutes, start, end)) return period.key
	}

	return null
}

export const getMilkingShiftLabel = (
	timestamp: number,
	company?: MilkingShiftCompanyPeriods | null,
): string => {
	const shift = getMilkingShiftByTimestamp(timestamp, company)
	return shift ? MILKING_SHIFT_LABELS[shift] : '—'
}
